package br.com.htms.inspeccaocampo.ui.screens

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.auth.AuthUserListItem
import br.com.htms.inspeccaocampo.data.logs.LogModulo
import br.com.htms.inspeccaocampo.data.logs.LogsModulosApi
import br.com.htms.inspeccaocampo.data.logs.ModuloLog
import br.com.htms.inspeccaocampo.data.logs.PlanilhaLogs
import br.com.htms.inspeccaocampo.data.logs.resumoDetalheLog
import br.com.htms.inspeccaocampo.data.logs.rotuloAcaoLog
import br.com.htms.inspeccaocampo.platform.PlatformFiles
import br.com.htms.inspeccaocampo.platform.formatDateTimeBr
import br.com.htms.inspeccaocampo.shared.ui.components.AppOutlinedTextField
import br.com.htms.inspeccaocampo.ui.kit.PortalButton
import br.com.htms.inspeccaocampo.ui.kit.PortalOutlinedButton
import br.com.htms.inspeccaocampo.ui.theme.PortalCores
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch

/** Admin: log separado de cada módulo, com exportação Excel do módulo ou de todos (uma aba por módulo). */
@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class, ExperimentalFoundationApi::class)
@Composable
fun SharedLogsModulosPanel(onMessage: (String) -> Unit) {
    var aberto by remember { mutableStateOf(false) }
    var modulo by remember { mutableStateOf(ModuloLog.ACESSO) }
    var cache by remember { mutableStateOf<Map<ModuloLog, List<LogModulo>>>(emptyMap()) }
    var loading by remember { mutableStateOf(false) }
    var exportando by remember { mutableStateOf<String?>(null) }
    var busca by remember { mutableStateOf("") }
    var filtroUser by remember { mutableStateOf("") }
    var users by remember { mutableStateOf<List<AuthUserListItem>>(emptyList()) }
    var userMenu by remember { mutableStateOf(false) }
    var filtroFrom by remember { mutableStateOf("") }
    var filtroTo by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()

    fun fromMs() = parseDiaLogMs(filtroFrom, fimDoDia = false)
    fun toMs() = parseDiaLogMs(filtroTo, fimDoDia = true)

    suspend fun buscar(m: ModuloLog): Result<List<LogModulo>> =
        LogsModulosApi.listar(m, fromMs(), toMs(), filtroUser.trim().ifBlank { null })

    fun carregar(m: ModuloLog) {
        scope.launch {
            loading = true
            buscar(m)
                .onSuccess { cache = cache + (m to it) }
                .onFailure { onMessage(it.message ?: "Falha ao carregar o log de ${m.rotulo}") }
            loading = false
        }
    }

    fun periodoTexto(): String {
        val de = filtroFrom.trim().ifBlank { "início" }
        val ate = filtroTo.trim().ifBlank { "hoje" }
        val u = filtroUser.trim().ifBlank { "todos os usuários" }
        return "$de até $ate · $u"
    }

    fun salvarExcel(nomeArquivo: String, porModulo: Map<ModuloLog, List<LogModulo>>, titulo: String) {
        runCatching {
            val path = PlatformFiles.join(PlatformFiles.cacheDir(), nomeArquivo)
            PlatformFiles.writeBytes(path, PlanilhaLogs.gerar(porModulo, periodoTexto()))
            br.com.htms.inspeccaocampo.platform.BackupSharePoint.com("Logs", "Logs dos módulos") { PlatformFiles.shareFile(path, PlanilhaLogs.MIME_XLSX, titulo) }
        }.onSuccess {
            onMessage("Excel gerado (${porModulo.values.sumOf { it.size }} registros).")
        }.onFailure { onMessage(it.message ?: "Falha ao gerar o Excel") }
    }

    fun exportarModulo() {
        val logs = cache[modulo].orEmpty()
        if (logs.isEmpty()) {
            onMessage("Nada para exportar em ${modulo.rotulo}.")
            return
        }
        salvarExcel("logs-${modulo.id.lowercase()}.xlsx", mapOf(modulo to logs), "Log ${modulo.rotulo}")
    }

    fun exportarTodos() {
        scope.launch {
            val resultado = linkedMapOf<ModuloLog, List<LogModulo>>()
            val falhas = mutableListOf<String>()
            ModuloLog.entries.forEachIndexed { i, m ->
                exportando = "Buscando ${m.rotulo} (${i + 1}/${ModuloLog.entries.size})..."
                buscar(m)
                    .onSuccess { resultado[m] = it }
                    .onFailure { falhas += m.rotulo; resultado[m] = emptyList() }
            }
            exportando = null
            cache = cache + resultado
            if (falhas.isNotEmpty()) onMessage("Sem resposta de: ${falhas.joinToString(", ")}")
            salvarExcel("logs-todos-modulos.xlsx", resultado, "Logs de todos os módulos")
        }
    }

    LaunchedEffect(aberto, modulo, filtroFrom, filtroTo, filtroUser) {
        if (!aberto) return@LaunchedEffect
        if (users.isEmpty()) {
            AuthApi.listUsers().onSuccess { list ->
                users = list
                    .filter { !it.username.equals("admin", ignoreCase = true) }
                    .sortedBy { it.username.lowercase() }
            }
        }
        while (isActive) {
            carregar(modulo)
            delay(20_000)
        }
    }

    val logsModulo = cache[modulo].orEmpty()
    val filtrados = remember(logsModulo, busca) {
        val q = busca.trim().lowercase()
        if (q.isEmpty()) logsModulo
        else logsModulo.filter {
            listOf(it.usuario, it.nome, it.acao, rotuloAcaoLog(it.acao, it.detalhe), it.alvo, it.detalhe).any { s -> s.lowercase().contains(q) }
        }
    }

    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            PortalOutlinedButton(onClick = { aberto = true }, modifier = Modifier.fillMaxWidth()) {
                Text("Logs por módulo")
            }
            Text(
                "Log separado de cada módulo (Acesso, Inspeções, REM, Ativos, Efetivo, Ocorrências...) " +
                    "OS, EPI, Certificados, APR e Dashboards, com Excel do módulo ou de todos.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }

    if (!aberto) return
    Dialog(
        onDismissRequest = { aberto = false },
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Box(Modifier.fillMaxSize()) {
            Box(
                Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.45f))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = { aberto = false }
                    )
            )
            Card(
                modifier = Modifier
                    .align(Alignment.Center)
                    .padding(horizontal = 16.dp, vertical = 20.dp)
                    .widthIn(max = 1000.dp)
                    .fillMaxWidth()
                    .fillMaxHeight(0.92f),
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                border = PortalCores.BordaCartao
            ) {
                Column(
                    Modifier.fillMaxSize().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            "Logs por módulo",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold,
                            color = PortalCores.Navy,
                            modifier = Modifier.weight(1f)
                        )
                        TextButton(onClick = { aberto = false }) { Text("✕") }
                    }
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        ModuloLog.entries.forEach { m ->
                            val qtd = cache[m]?.size
                            FilterChip(
                                selected = modulo == m,
                                onClick = { modulo = m; busca = "" },
                                label = { Text(if (qtd != null) "${m.rotulo} ($qtd)" else m.rotulo) }
                            )
                        }
                    }
                    Text(
                        modulo.descricao,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Row(horizontalArrangement = Arrangement.spacedBy(6.dp), modifier = Modifier.fillMaxWidth()) {
                        br.com.htms.inspeccaocampo.ui.kit.SeletorBusca(
                            label = "Usuário",
                            opcoes = listOf("" to "Todos") + users.map { it.username to it.username },
                            selecionado = filtroUser,
                            onSelect = { filtroUser = it },
                            modifier = Modifier.weight(1.2f)
                        )
                        AppOutlinedTextField(
                            value = filtroFrom,
                            onValueChange = { filtroFrom = it },
                            label = { Text("De (dd/mm/aaaa)") },
                            singleLine = true,
                            modifier = Modifier.weight(1f)
                        )
                        AppOutlinedTextField(
                            value = filtroTo,
                            onValueChange = { filtroTo = it },
                            label = { Text("Até (dd/mm/aaaa)") },
                            singleLine = true,
                            modifier = Modifier.weight(1f)
                        )
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalAlignment = Alignment.CenterVertically) {
                        AppOutlinedTextField(
                            value = busca,
                            onValueChange = { busca = it },
                            label = { Text("Buscar no módulo") },
                            singleLine = true,
                            modifier = Modifier.weight(1f)
                        )
                        TextButton(
                            onClick = {
                                cache = emptyMap()
                                carregar(modulo)
                            },
                            enabled = !loading && exportando == null
                        ) { Text("Aplicar filtros") }
                    }
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            exportando ?: if (loading) "Carregando..." else
                                "${modulo.rotulo}: exibindo ${filtrados.size} de ${logsModulo.size}",
                            fontWeight = FontWeight.SemiBold,
                            modifier = Modifier.weight(1f)
                        )
                        PortalOutlinedButton(
                            onClick = { exportarModulo() },
                            enabled = !loading && exportando == null && logsModulo.isNotEmpty()
                        ) { Text("Excel do módulo") }
                        PortalButton(
                            onClick = { exportarTodos() },
                            enabled = !loading && exportando == null
                        ) { Text("Excel de todos os módulos") }
                    }
                    val rolagem294 = androidx.compose.foundation.lazy.rememberLazyListState()
                    br.com.htms.inspeccaocampo.ui.kit.AreaRolavel(rolagem294, Modifier.fillMaxWidth().weight(1f)) {
                    LazyColumn(
                        state = rolagem294,
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        items(filtrados) { LinhaLogModulo(it) }
                        if (!loading && modulo in cache && filtrados.isEmpty()) {
                            item {
                                Text(
                                    if (logsModulo.isEmpty()) "Nenhum registro neste módulo para os filtros."
                                    else "Nenhum registro para a busca.",
                                    style = MaterialTheme.typography.bodySmall
                                )
                            }
                        }
                    }
                    }
                }
            }
        }
    }
}

@Composable
private fun LinhaLogModulo(e: LogModulo) {
    var expand by remember(e) { mutableStateOf(false) }
    Card(
        modifier = Modifier.fillMaxWidth().clickable(
            interactionSource = remember { MutableInteractionSource() },
            indication = null,
            onClick = { expand = !expand }
        ),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.35f)
        ),
        shape = RoundedCornerShape(8.dp)
    ) {
        Column(
            Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 5.dp),
            verticalArrangement = Arrangement.spacedBy(2.dp)
        ) {
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalAlignment = Alignment.CenterVertically) {
                Text(
                    rotuloAcaoLog(e.acao, e.detalhe),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.weight(1f),
                    maxLines = 1
                )
                Text(
                    e.nome.ifBlank { e.usuario }.ifBlank { "—" },
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1
                )
                Text(
                    formatDateTimeBr(e.createdAt),
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1
                )
            }
            val resumo = resumoDetalheLog(e.alvo, e.detalhe)
            if (resumo.isNotBlank()) {
                Text(
                    resumo,
                    style = MaterialTheme.typography.labelSmall,
                    maxLines = if (expand) Int.MAX_VALUE else 1
                )
            }
            if (expand) {
                Text("Código: ${e.acao}", style = MaterialTheme.typography.labelSmall)
                if (e.usuario.isNotBlank()) Text("Usuário: ${e.usuario}", style = MaterialTheme.typography.labelSmall)
                if (e.ip.isNotBlank()) Text("IP: ${e.ip}", style = MaterialTheme.typography.labelSmall)
            }
        }
    }
}

private fun parseDiaLogMs(raw: String, fimDoDia: Boolean): Long? {
    val parts = raw.trim().takeIf { it.isNotEmpty() }?.split("/", "-", ".") ?: return null
    if (parts.size != 3) return null
    val d = parts[0].toIntOrNull() ?: return null
    val m = parts[1].toIntOrNull() ?: return null
    val y = parts[2].toIntOrNull() ?: return null
    if (y < 2000 || m !in 1..12 || d !in 1..31) return null
    var yy = y.toLong()
    var mm = m.toLong()
    if (mm <= 2) { yy -= 1; mm += 12 }
    val era = if (yy >= 0) yy / 400 else (yy - 399) / 400
    val yoe = yy - era * 400
    val doy = (153 * (mm - 3) + 2) / 5 + d - 1
    val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
    val inicio = (era * 146097 + doe - 719468) * 86_400_000L
    return if (fimDoDia) inicio + 86_400_000L - 1L else inicio
}
